/**
 * Lop tuong thich 2Captcha API v2 — hop dong CHINH cua capforge.
 * Doi chieu: https://2captcha.com/api-docs (createTask / getTaskResult / getBalance /
 * reportCorrect / reportIncorrect), kiem tra lai 2026-07.
 *
 * Shape o day phai dung tuyet doi: day la dieu kien de mot project dang goi 2Captcha
 * chi doi `baseUrl` la chay duoc.
 */
import { z } from 'zod';
import { TWO_CAPTCHA_ERRORS } from './errors.js';
// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------
/**
 * Phan mo rong rieng cua capforge trong body createTask. KHONG bat buoc.
 * Muc dich: cho client chi thang profile thay vi de server phai doan (muc 8).
 */
export const CapforgeExtensionSchema = z
    .object({
    profileKey: z.string().min(1).optional(),
    siteHost: z.string().min(1).optional(),
})
    .catchall(z.unknown());
/**
 * Task chi bat buoc co `type`. Moi truong khac de mo:
 * 2Captcha co hang chuc task type, va client that su gui rat nhieu truong phu.
 * Validate chat o day se pha vo tinh drop-in.
 */
export const AnyTaskSchema = z
    .object({
    type: z.string().min(1),
})
    .catchall(z.unknown());
export const CreateTaskRequestSchema = z
    .object({
    clientKey: z.string().min(1),
    task: AnyTaskSchema,
    languagePool: z.string().optional(),
    callbackUrl: z.string().optional(),
    softId: z.number().int().optional(),
    capforge: CapforgeExtensionSchema.optional(),
})
    .catchall(z.unknown());
export const GetTaskResultRequestSchema = z
    .object({
    clientKey: z.string().min(1),
    taskId: z.number().int().positive(),
})
    .catchall(z.unknown());
export const GetBalanceRequestSchema = z
    .object({
    clientKey: z.string().min(1),
})
    .catchall(z.unknown());
export const ReportRequestSchema = z
    .object({
    clientKey: z.string().min(1),
    taskId: z.number().int().positive(),
})
    .catchall(z.unknown());
/**
 * Nguong kich thuoc anh.
 *
 * Chi ap CAN DUOI: duoi 100 byte thi chac chan khong phai anh that (2Captcha cung dung
 * moc nay, errorId 3). CAN TREN 100kB cua 2Captcha thi capforge KHONG ap dat — do la
 * gioi han thuong mai cua ho, con ta tu host nen chan anh lon la tu lam kho minh.
 */
export const MIN_IMAGE_BYTES = 100;
// ---------------------------------------------------------------------------
// Solution — shape phai dung tuyet doi theo bang o muc 6.1
// ---------------------------------------------------------------------------
export const ImageToTextSolutionSchema = z.object({ text: z.string() });
export const TextCaptchaSolutionSchema = z.object({ text: z.string() });
/** So o, DANH SO TU 1, trai -> phai roi tren -> duoi. */
export const GridSolutionSchema = z.object({ click: z.array(z.number().int().positive()) });
export const CoordinatesSolutionSchema = z.object({
    coordinates: z.array(z.tuple([z.number(), z.number()])),
});
/** Do, thuan chieu kim dong ho. */
export const RotateSolutionSchema = z.object({ rotate: z.number() });
export const BoundingBoxSolutionSchema = z.object({ bounding_boxes: z.array(z.unknown()) });
export const DrawAroundSolutionSchema = z.object({ canvas: z.array(z.unknown()) });
/** Task token: passthrough nguyen van tu upstream, khong duoc nan lai. */
export const TokenSolutionSchema = z.record(z.string(), z.unknown());
/** Tra cuu schema solution theo task type — contract test dua vao bang nay. */
export const SOLUTION_SCHEMA_BY_TASK_TYPE = {
    ImageToTextTask: ImageToTextSolutionSchema,
    TextCaptchaTask: TextCaptchaSolutionSchema,
    GridTask: GridSolutionSchema,
    CoordinatesTask: CoordinatesSolutionSchema,
    RotateTask: RotateSolutionSchema,
    BoundingBoxTask: BoundingBoxSolutionSchema,
    DrawAroundTask: DrawAroundSolutionSchema,
};
// ---------------------------------------------------------------------------
// Phan loai loi validate -> error code 2Captcha dung
// ---------------------------------------------------------------------------
/**
 * Doi mot body createTask sai dinh dang thanh dung ma loi ma 2Captcha se tra.
 * Tach ra day de server va contract test khong the lech nhau.
 *
 * Thu tu kiem tra bam theo hanh vi that cua 2Captcha:
 *   thieu clientKey  -> ERROR_KEY_DOES_NOT_EXIST
 *   thieu task       -> ERROR_TASK_ABSENT
 *   task thieu type  -> ERROR_TASK_NOT_SUPPORTED
 *   con lai          -> ERROR_BAD_PARAMETERS
 */
export function classifyCreateTaskBody(body) {
    if (typeof body !== 'object' || body === null)
        return 'ERROR_BAD_PARAMETERS';
    const raw = body;
    if (typeof raw['clientKey'] !== 'string' || raw['clientKey'].length === 0) {
        return 'ERROR_KEY_DOES_NOT_EXIST';
    }
    const task = raw['task'];
    if (task === undefined || task === null)
        return 'ERROR_TASK_ABSENT';
    if (typeof task !== 'object' || Array.isArray(task))
        return 'ERROR_TASK_ABSENT';
    const type = task['type'];
    if (typeof type !== 'string' || type.length === 0)
        return 'ERROR_TASK_NOT_SUPPORTED';
    const parsed = CreateTaskRequestSchema.safeParse(body);
    if (!parsed.success)
        return 'ERROR_BAD_PARAMETERS';
    return null;
}
/** Doi mot body {clientKey, taskId} sai dinh dang thanh ma loi dung. */
export function classifyTaskIdBody(body) {
    if (typeof body !== 'object' || body === null)
        return 'ERROR_BAD_PARAMETERS';
    const raw = body;
    if (typeof raw['clientKey'] !== 'string' || raw['clientKey'].length === 0) {
        return 'ERROR_KEY_DOES_NOT_EXIST';
    }
    const taskId = raw['taskId'];
    if (typeof taskId !== 'number' || !Number.isInteger(taskId) || taskId <= 0) {
        return 'ERROR_NO_SUCH_CAPCHA_ID';
    }
    return null;
}
/** Tap hop errorId hop le — contract test dung de khang dinh khong co code tu che. */
export const VALID_ERROR_IDS = new Set(Object.values(TWO_CAPTCHA_ERRORS).map((e) => e.errorId));
//# sourceMappingURL=twocaptcha.js.map